/* eslint-disable max-len */
import { datadogRum } from '@datadog/browser-rum';

type PerformanceResourceEntries = {
  fetch: PerformanceResourceTiming[];
  assets: PerformanceResourceTiming[];
}

type PerformanceData = {
  rootLoadTime: number;
  resources: PerformanceResourceTiming[];
  fetchRequests: PerformanceResourceTiming[];
};

type PerformanceCategoryLevel = {
  root: PerformanceTimingLevel;
  asset: PerformanceTimingLevel;
  fetch: PerformanceTimingLevel;
};

type PerformanceLevel = 1 | 2 | 3;

type PerformanceTimingLevel = {
  time: number | null;
  level: PerformanceLevel;
}

export class NetworkPerformanceTracker {

  private static instance: NetworkPerformanceTracker;

  private observer: PerformanceObserver | null = null;

  private performanceResourceEntries: PerformanceResourceEntries = { fetch: [], assets: [] };

  private isTracking = false;

  // eslint-disable-next-line no-useless-constructor, no-empty-function
  private constructor() {}

  static getInstance(): NetworkPerformanceTracker {
    if (!NetworkPerformanceTracker.instance) {
      NetworkPerformanceTracker.instance = new NetworkPerformanceTracker();
    }
    return NetworkPerformanceTracker.instance;
  }

  startTracking(): void {
    if (!window.PerformanceObserver || this.isTracking) return;

    this.isTracking = true;
    this.performanceResourceEntries = { fetch: [], assets: [] };

    this.observer = new PerformanceObserver((entryList) => {
      const entries = entryList.getEntriesByType('resource') as PerformanceResourceTiming[];
      entries.forEach((entry) => {
        if (entry.duration > 0) {
          if (entry.initiatorType === 'fetch' || entry.initiatorType === 'xmlhttprequest') {
            this.performanceResourceEntries.fetch.push(entry);
          } else {
            this.performanceResourceEntries.assets.push(entry);
          }
        }
      });
    });

    this.observer.observe({ type: 'resource', buffered: true });
  }

  stopTracking(): void {
    if (!this.isTracking) return;

    this.isTracking = false;

    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }

    this.postComputedNetworkScore();
  }

  private hasTrackedResourceEntries(): boolean {
    return this.performanceResourceEntries.fetch.length > 0 || this.performanceResourceEntries.assets.length > 0;
  }

  private postComputedNetworkScore(): void {
    if (!this.hasTrackedResourceEntries()) return;

    const [ navigation ] = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
    const rootLoadTime = (navigation.loadEventEnd - navigation.startTime);
    const networkScore = NetworkPerformanceTracker.categorizePerformance(
      {
        rootLoadTime,
        fetchRequests: this.performanceResourceEntries.fetch,
        resources: this.performanceResourceEntries.assets,
      }
    );

    datadogRum.addAction('networkScore', networkScore);
  }

  static getPerformanceLevel(loadingTime: number, performanceTiming: PerformanceTimingLevel[]): PerformanceLevel {
    return performanceTiming.find((performanceTimingEntry) => {
      if (performanceTimingEntry.time === null) {
        return true;
      }
      return loadingTime <= performanceTimingEntry.time;
    })?.level ?? 3;
  }

  static categorizePerformance(
    { rootLoadTime, resources, fetchRequests }: PerformanceData
  ): PerformanceCategoryLevel {
    const rootLevel = NetworkPerformanceTracker.getPerformanceLevel(rootLoadTime, RootPerformanceTiming);
    const assetMaxLoadingTime = Math.max(...resources.map((r) => r.duration), 0);
    const assetLevel = NetworkPerformanceTracker.getPerformanceLevel(assetMaxLoadingTime, AssetPerformanceTiming);
    const fetchMaxLoadingTime = Math.max(...fetchRequests.map((r) => r.duration), 0);
    const fetchLevel = NetworkPerformanceTracker.getPerformanceLevel(fetchMaxLoadingTime, FetchPerformanceTiming);

    return {
      root: { time: rootLoadTime, level: rootLevel },
      asset: { time: assetMaxLoadingTime, level: assetLevel },
      fetch: { time: fetchMaxLoadingTime, level: fetchLevel },
    };
  }

}

const RootPerformanceTiming: PerformanceTimingLevel[] = [ { level: 1, time: 1500 }, { level: 2, time: 4000 }, { level: 3, time: null } ];

const AssetPerformanceTiming: PerformanceTimingLevel[] = [ { level: 1, time: 50 }, { level: 2, time: 150 }, { level: 3, time: null } ];

const FetchPerformanceTiming : PerformanceTimingLevel[] = [ { level: 1, time: 200 }, { level: 2, time: 500 }, { level: 3, time: null } ];
